<script setup lang="ts">
import { ref, computed, watch, inject, onBeforeUnmount, type ComputedRef } from 'vue'
import type { RemovableRef } from '@vueuse/core'
import { useTruckStore } from '@/stores/trucks'
import { EmptyableMediaType, type EmptyTruck } from '@/forms/EmptyTruck'

const formState: RemovableRef<EmptyTruck> = inject('formState') as RemovableRef<EmptyTruck>
const showFullForm: ComputedRef<boolean> = inject('showFullForm') as ComputedRef<boolean>
const needsQuantityAndDocument: ComputedRef<boolean> = inject(
  'needsQuantityAndDocument'
) as ComputedRef<boolean>

const truckStore = useTruckStore()
const truck = computed(() =>
  formState.value.truckParkCode ? truckStore.getTruck(formState.value.truckParkCode) : null
)

const formatQuantity = (parts: Array<[number | undefined, string]>) =>
  parts
    .filter(([val]) => !!val)
    .map(([val, unit]) => `${val} ${unit}`)
    .join(' et ') || '0'

const quantityInTruck = computed(() => {
  if (formState.value.mediaType === EmptyableMediaType.HardDrive) {
    return formatQuantity([
      [truck.value?.hardDriveM3, 'm3'],
      [truck.value?.hardDriveContainer, 'conteneur(s)'],
      [truck.value?.hardDriveNumber, 'unité(s)'],
      [truck.value?.hardDriveWeight, 'kg']
    ])
  } else if (formState.value.mediaType === EmptyableMediaType.DIB) {
    return `${truck.value?.dibNumber} unités et ${truck.value?.dibWeight} kg`
  } else if (formState.value.mediaType === EmptyableMediaType.ElectronicalComponents) {
    return formatQuantity([
      [truck.value?.electronicalComponentsM3, 'm3'],
      [truck.value?.electronicalComponentsContainer, 'conteneur(s)'],
      [truck.value?.electronicalComponentsNumber, 'unité(s)'],
      [truck.value?.electronicalComponentsWeight, 'kg']
    ])
  }
  return `${truck.value?.wasteWeight} kg`
})

const defaultUnit = 'kg'

watch(needsQuantityAndDocument, () => {
  if (truck.value && !needsQuantityAndDocument.value) {
    if (formState.value.mediaType === EmptyableMediaType.HardDrive) {
      formState.value.quantity = truck.value.hardDriveWeight || null
    } else if (formState.value.mediaType === EmptyableMediaType.ElectronicalComponents) {
      formState.value.quantity = truck.value.electronicalComponentsWeight || null
    } else {
      formState.value.quantity = truck.value.dibWeight
    }
    formState.value.unity = defaultUnit
  } else {
    formState.value.quantity = null
    formState.value.unity = null
  }
})

const online = ref(navigator.onLine)
const onlineHandler = () => (online.value = true)
const offlineHandler = () => (online.value = false)

window.addEventListener('online', onlineHandler)
window.addEventListener('offline', offlineHandler)

onBeforeUnmount(() => {
  window.removeEventListener('online', onlineHandler)
  window.removeEventListener('offline', offlineHandler)
})
</script>

<template>
  <v-sheet class="quantity px-3 pt-2" rounded="lg" v-if="showFullForm">
    <v-container>
      <v-row>
        <v-col
          cols="12"
          :sm="needsQuantityAndDocument ? 6 : 12"
          :class="{
            'd-flex': !needsQuantityAndDocument,
            'pb-1': needsQuantityAndDocument,
            'pb-5': !needsQuantityAndDocument
          }"
        >
          <h3 class="mr-2">Quantité dans le camion :</h3>
          <p
            :class="{
              'quantity-in-truck': true,
              'font-italic': !online,
              inline: !needsQuantityAndDocument
            }"
          >
            <template v-if="online">{{ quantityInTruck }}</template>
            <template v-else>Indisponible hors connexion</template>
          </p>
        </v-col>
        <v-col cols="12" sm="6" class="pb-1" v-if="needsQuantityAndDocument">
          <v-label class="required">Quantitée vidée</v-label>
          <v-text-field class="quantity-destroyed" type="number" v-model="formState.quantity">
            <template #append-inner>{{ defaultUnit }}</template>
          </v-text-field>
        </v-col>
      </v-row>
    </v-container>
  </v-sheet>
</template>

<style scoped lang="scss">
.quantity {
  .quantity-in-truck {
    display: flex;
    align-items: center;
    &:not(.inline) {
      min-height: 48px;
    }
  }
  .quantity-destroyed:deep(.v-field__append-inner) {
    font-size: 0.75rem;
  }
}
</style>
